import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Star, ShieldCheck, Lock, CreditCard, BookOpen, Zap, FileText, Gift, Clock, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { PurchasePopup } from '../components/PurchasePopup';
import { trackFbEvent } from '../lib/fpixel';
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 37
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          ...prev,
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex items-center justify-center gap-4 text-3xl md:text-5xl font-bold text-brand-violet my-6" data-id="element-12">
      <div className="flex flex-col items-center" data-id="element-13">
        <span className="bg-brand-violet/10 px-4 py-2 rounded-lg" data-id="element-14">
          {String(timeLeft.hours).padStart(2, '0')}
        </span>
        <span className="text-sm text-gray-500 mt-2 font-normal" data-id="element-15">HS</span>
      </div>
      <span className="pb-8" data-id="element-16">:</span>
      <div className="flex flex-col items-center" data-id="element-17">
        <span className="bg-brand-violet/10 px-4 py-2 rounded-lg" data-id="element-18">
          {String(timeLeft.minutes).padStart(2, '0')}
        </span>
        <span className="text-sm text-gray-500 mt-2 font-normal" data-id="element-19">MIN</span>
      </div>
      <span className="pb-8" data-id="element-20">:</span>
      <div className="flex flex-col items-center" data-id="element-21">
        <span className="bg-brand-violet/10 px-4 py-2 rounded-lg" data-id="element-22">
          {String(timeLeft.seconds).padStart(2, '0')}
        </span>
        <span className="text-sm text-gray-500 mt-2 font-normal" data-id="element-23">SEG</span>
      </div>
    </div>;
};
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 py-4" data-id="element-24">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full justify-between items-center text-left font-semibold text-brand-black hover:text-brand-violet transition-colors" data-id="element-25">
        <span data-id="element-26">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 flex-shrink-0" data-id="element-27" /> : <ChevronDown className="w-5 h-5 flex-shrink-0" data-id="element-28" />}
      </button>
      {isOpen && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="mt-3 text-gray-600" data-id="element-29">
          {answer}
        </motion.div>}
    </div>;
};
export function LandingPage() {
  const [selectedPack, setSelectedPack] = useState<'pro' | 'elite'>('elite');
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-brand-white font-sans text-brand-black" data-id="element-30">
      <PurchasePopup data-id="element-31" />

      {/* BLOQUE 1 — HERO */}
      <section className="relative bg-brand-violet text-brand-white pb-16 md:pb-24 px-4 overflow-hidden" data-id="element-32">
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1518611012118-696072aa579a?auto=format&fit=crop&q=80')] bg-cover bg-center" data-id="element-33" />
        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-34">
          {/* Above-the-fold block: fits within one mobile screen, top of page → first CTA */}
          <div className="flex flex-col justify-center items-center min-h-[100svh] md:min-h-0 md:pt-20 py-4" data-id="element-35">
            <div className="inline-block bg-brand-gold text-white font-bold px-4 py-1 rounded-full text-[11px] md:text-sm mb-3 md:mb-6 uppercase tracking-wider" data-id="element-36">
              🎀 Profe Lista · Gimnasia Rítmica 360
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-6xl font-bold mb-3 md:mb-6 leading-tight" data-id="element-37">
              Todo tu año de Gimnasia Rítmica{' '}
              <span className="text-brand-gold" data-id="element-38">resuelto de una vez.</span>
            </h1>

            <img src="https://cdn.magicpatterns.com/uploads/jP8SYEMpHmkQrqAWmmbJQj/g_ritmica_mock.png" alt="Megapack Gimnasia Rítmica 360 — Plan Anual Completo y Recursos" className="w-full max-w-[210px] sm:max-w-xs md:max-w-xl mx-auto mb-3 md:mb-8 drop-shadow-2xl" data-id="element-39" />

            <p className="text-sm sm:text-base md:text-2xl mb-3 md:mb-8 text-purple-100 max-w-3xl mx-auto" data-id="element-40">
              El sistema completo de planificación y clases resueltas para el
              profesor de Gimnasia Rítmica argentino.
            </p>

            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-4 md:mb-10 text-[11px] md:text-base font-medium" data-id="element-41">
              <span className="flex items-center gap-1.5 md:gap-2 bg-white/10 px-3 py-1.5 md:px-4 md:py-2 rounded-lg" data-id="element-42">
                <FileText className="w-4 h-4 md:w-5 md:h-5 text-brand-gold" data-id="element-43" />{' '}
                PDF
              </span>
              <span className="flex items-center gap-1.5 md:gap-2 bg-white/10 px-3 py-1.5 md:px-4 md:py-2 rounded-lg" data-id="element-44">
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-brand-gold" data-id="element-45" /> Acceso
                Inmediato
              </span>
              <span className="flex items-center gap-1.5 md:gap-2 bg-white/10 px-3 py-1.5 md:px-4 md:py-2 rounded-lg" data-id="element-46">
                <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-brand-gold" data-id="element-47" />{' '}
                12 Tomos + 6 Bonos + 2 Elite
              </span>
            </div>

            <button onClick={scrollToPricing} className="bg-brand-gold hover:bg-amber-500 text-white text-base md:text-xl font-bold py-4 md:py-5 px-8 md:px-10 rounded-full shadow-[0_0_30px_rgba(217,119,6,0.5)] transition-all transform hover:scale-105 flex items-center justify-center gap-3 mx-auto w-full md:w-auto" data-id="element-48">
              💜 QUIERO MI GIMNASIA RÍTMICA 360 AHORA
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mt-6 text-sm text-purple-200" data-id="element-49">
            <span className="flex items-center gap-1" data-id="element-50">
              <ShieldCheck className="w-4 h-4" data-id="element-51" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-52">
              <Lock className="w-4 h-4" data-id="element-53" /> Pago seguro
            </span>
            <span className="flex items-center gap-1" data-id="element-54">
              <CreditCard className="w-4 h-4" data-id="element-55" /> Tarjetas y transferencia
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 pt-12 border-t border-white/20" data-id="element-56">
            <div data-id="element-57">
              <div className="text-3xl font-bold text-brand-gold" data-id="element-58">+300</div>
              <div className="text-sm" data-id="element-59">profes confían</div>
            </div>
            <div data-id="element-60">
              <div className="text-3xl font-bold text-brand-gold" data-id="element-61">12+6</div>
              <div className="text-sm" data-id="element-62">Tomos + Bonos</div>
            </div>
            <div data-id="element-63">
              <div className="text-3xl font-bold text-brand-gold" data-id="element-64">4.9/5</div>
              <div className="text-sm" data-id="element-65">calificación</div>
            </div>
            <div data-id="element-66">
              <div className="text-3xl font-bold text-brand-gold" data-id="element-67">⭐ 🇦🇷</div>
              <div className="text-sm" data-id="element-68">Argentina</div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOQUE 2 — TESTIMONIOS HERO */}
      <section className="py-12 bg-gray-50 px-4" data-id="element-69">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6" data-id="element-70">
          {[{
          initials: 'MV',
          text: '"Organicé todo el mes en una tarde. Nunca más llego al gimnasio sin saber qué dar."',
          author: 'Marcela V., Buenos Aires',
          role: 'Profesora de Rítmica en Club'
        }, {
          initials: 'CG',
          text: '"Mis alumnas de Jardín progresaron en coordinación desde la primera semana con el Tomo 1."',
          author: 'Carolina G., Córdoba',
          role: 'Profe de Rítmica en Academia Privada'
        }, {
          initials: 'SR',
          text: '"Antes tardaba 2 horas armando una clase. Ahora son 5 minutos."',
          author: 'Silvana R., Rosario',
          role: 'Profesora de Rítmica Independiente'
        }].map((t, i) => <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative" data-id="element-71">
              <div className="absolute -top-4 left-6 bg-brand-violet text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" data-id="element-72">
                {t.initials}
              </div>
              <div className="flex text-brand-gold mb-3 mt-2" data-id="element-73">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-74" />)}
              </div>
              <p className="text-gray-700 italic mb-4" data-id="element-75">"{t.text}"</p>
              <div className="text-sm font-semibold text-brand-black" data-id="element-76">
                {t.author}
              </div>
              <div className="text-xs text-gray-500" data-id="element-77">{t.role}</div>
            </div>)}
        </div>
      </section>

      {/* BLOQUE 3 — DOLOR */}
      <section className="py-20 px-4 bg-white" data-id="element-78">
        <div className="max-w-4xl mx-auto" data-id="element-79">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-80">
            Si sos profe de Rítmica, esto{' '}
            <span className="text-brand-violet" data-id="element-81">te va a sonar conocido:</span>
          </h2>

          <div className="space-y-4 mb-12" data-id="element-82">
            {['Llegás al gimnasio sin saber exactamente qué vas a dar en la clase de hoy', 'Tus alumnas no progresan porque no hay un orden claro de contenidos técnicos semana a semana', 'Tenés niveles mezclados (Jardín, Escuela, Juvenil) y no sabés cómo organizar cada grupo al mismo tiempo', 'Cuando hay feriado o ausencia perdés la continuidad del entrenamiento y todo se desordena', 'No podés justificar un aumento de cuota porque no tenés nada sistematizado ni documentado', 'Los padres te hacen preguntas sobre el progreso de sus hijas y no tenés cómo demostrar los resultados', 'Sentís que cada año empezás de cero con la planificación aunque ya tengas años de experiencia', 'Invertís horas del domingo a la noche preparando clases que podrían estar resueltas en 5 minutos'].map((pain, i) => <div key={i} className="flex items-start gap-4 p-4 rounded-xl hover:bg-red-50 transition-colors" data-id="element-83">
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-84" />
                <p className="text-lg text-gray-700" data-id="element-85">{pain}</p>
              </div>)}
          </div>

          <div className="text-center bg-brand-violet/5 p-8 rounded-2xl border border-brand-violet/20" data-id="element-86">
            <p className="text-2xl font-bold text-brand-black mb-6" data-id="element-87">
              "¿Cuántas clases más vas a improvisar?"
            </p>
            <button onClick={scrollToPricing} className="bg-brand-violet hover:bg-purple-700 text-white text-lg font-bold py-4 px-8 rounded-full transition-all flex items-center justify-center gap-2 mx-auto w-full md:w-auto" data-id="element-88">
              👉 BASTA DE IMPROVISAR — QUIERO MI GIMNASIA RÍTMICA 360
            </button>
          </div>
        </div>
      </section>

      {/* BLOQUE 4 — PRESENTACIÓN DEL PRODUCTO */}
      <section className="py-20 px-4 bg-brand-black text-white" data-id="element-89">
        <div className="max-w-5xl mx-auto text-center" data-id="element-90">
          <div className="inline-flex items-center gap-2 text-brand-gold font-bold mb-6" data-id="element-91">
            <Star className="fill-current w-6 h-6" data-id="element-92" />
            <span className="uppercase tracking-widest" data-id="element-93">
              El Sistema N°1 para Profesores de Gimnasia Rítmica Argentinos
            </span>
            <Star className="fill-current w-6 h-6" data-id="element-94" />
          </div>

          <h2 className="text-4xl md:text-5xl font-bold mb-8" data-id="element-95">
            GIMNASIA RÍTMICA 360 — Colección Completa
          </h2>
          <p className="text-2xl text-gray-300 mb-12" data-id="element-96">
            12 Tomos + 6 Bonos + 2 Elite — Todo resuelto, todos los niveles.
          </p>

          <img src="https://images.unsplash.com/photo-1541534741688-6078c6bfb5c5?auto=format&fit=crop&q=80&w=1200" alt="Gimnasia Rítmica 360 Product" className="w-full max-w-3xl mx-auto rounded-2xl shadow-2xl mb-12 border-4 border-brand-violet/30" data-id="element-97" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto" data-id="element-98">
            <div className="bg-white/10 p-6 rounded-xl" data-id="element-99">
              <div className="text-3xl font-bold text-brand-gold mb-2" data-id="element-100">
                +290
              </div>
              <div className="text-sm text-gray-300" data-id="element-101">páginas</div>
            </div>
            <div className="bg-white/10 p-6 rounded-xl" data-id="element-102">
              <div className="text-3xl font-bold text-brand-gold mb-2" data-id="element-103">
                +200
              </div>
              <div className="text-sm text-gray-300" data-id="element-104">
                ejercicios y secuencias
              </div>
            </div>
            <div className="bg-white/10 p-6 rounded-xl" data-id="element-105">
              <div className="text-xl font-bold text-brand-gold mb-2 leading-tight" data-id="element-106">
                Todos los
              </div>
              <div className="text-sm text-gray-300" data-id="element-107">niveles</div>
            </div>
            <div className="bg-white/10 p-6 rounded-xl" data-id="element-108">
              <div className="text-3xl font-bold text-brand-gold mb-2" data-id="element-109">52</div>
              <div className="text-sm text-gray-300" data-id="element-110">
                semanas resueltas por nivel
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOQUE 5 — LOS 12 TOMOS */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-111">
        <div className="max-w-6xl mx-auto" data-id="element-112">
          <div className="text-center mb-16" data-id="element-113">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-114">
              Los 12 Tomos Completos
            </h2>
            <p className="text-xl text-gray-600" data-id="element-115">
              Clases resueltas · Por nivel y categoría · Listo para usar mañana
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-116">
            {[{
            title: 'TOMO 1 · Jardín',
            subtitle: 'Semanas 1 a 17',
            desc: 'Exploración del cuerpo, espacio y ritmo. Primer contacto con los 5 aparatos. Juego y creatividad.'
          }, {
            title: 'TOMO 2 · Jardín',
            subtitle: 'Semanas 18 a 35',
            desc: 'Profundización de aparatos. Expresión corporal y musicalidad. Cooperación y trabajo grupal.'
          }, {
            title: 'TOMO 3 · Jardín',
            subtitle: 'Semanas 36 a 52',
            desc: 'Combinaciones básicas. Preparación y muestra de fin de año. Cierre institucional del ciclo.'
          }, {
            title: 'TOMO 4 · Escuela Femenina',
            subtitle: 'Semanas 1 a 17',
            desc: 'Técnica corporal base. Primeras dificultades de nivel. Introducción al trabajo con música.'
          }, {
            title: 'TOMO 5 · Escuela Femenina',
            subtitle: 'Semanas 18 a 35',
            desc: 'Consolidación técnica. Drills de aparatos. Primeras secuencias encadenadas.'
          }, {
            title: 'TOMO 6 · Escuela Femenina',
            subtitle: 'Semanas 36 a 52',
            desc: 'Armado de series para muestra o torneo. Cierre técnico anual con evaluación.'
          }, {
            title: 'TOMO 7 · Juvenil',
            subtitle: 'Semanas 1 a 17',
            desc: 'Dificultades corporales de nivel. Pivots y saltos técnicos. Trabajo específico de aparato.'
          }, {
            title: 'TOMO 8 · Juvenil',
            subtitle: 'Semanas 18 a 35',
            desc: 'Series de 75–90 seg. Combos encadenados. Preparación para torneos regionales.'
          }, {
            title: 'TOMO 9 · Juvenil',
            subtitle: 'Semanas 36 a 52',
            desc: 'Refinamiento técnico y artístico. Temporada competitiva y muestra final.'
          }, {
            title: 'TOMO 10 · Avanzada y Proyección',
            subtitle: 'Semanas 1 a 17',
            desc: 'Alto rendimiento. Dificultades de nivel nacional. Metodología de entrenamiento intensivo.'
          }, {
            title: 'TOMO 11 · Avanzada y Proyección',
            subtitle: 'Semanas 18 a 35',
            desc: 'Series completas de competencia. Estrategia de tapiz. Trabajo mental y gestión del error.'
          }, {
            title: 'TOMO 12 · Avanzada y Proyección',
            subtitle: 'Semanas 36 a 52',
            desc: 'Temporada de torneos. Plan pre-competencia de 8 semanas. Evaluación final y diagnóstico para el año siguiente.'
          }].map((tomo, i) => <div key={i} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow border-t-4 border-t-brand-violet" data-id="element-117">
                <h3 className="font-bold text-lg text-brand-black" data-id="element-118">
                  {tomo.title}
                </h3>
                <p className="text-sm text-brand-violet font-semibold mb-3" data-id="element-119">
                  {tomo.subtitle}
                </p>
                <p className="text-gray-600 text-sm" data-id="element-120">{tomo.desc}</p>
              </div>)}
          </div>

          <div className="mt-12 bg-brand-violet text-white p-8 rounded-2xl flex flex-wrap justify-center gap-8 text-center" data-id="element-121">
            <div className="flex items-center gap-2" data-id="element-122">
              <span className="text-2xl" data-id="element-123">✏️</span> Ejercicios y drills
              detallados
            </div>
            <div className="flex items-center gap-2" data-id="element-124">
              <span className="text-2xl" data-id="element-125">⚡</span> Sistema "Abrí y Dictás"
            </div>
            <div className="flex items-center gap-2" data-id="element-126">
              <span className="text-2xl" data-id="element-127">📋</span> Planillas listas para
              imprimir
            </div>
            <div className="flex items-center gap-2" data-id="element-128">
              <span className="text-2xl" data-id="element-129">🇦🇷</span> Contenido 100% adaptado
            </div>
          </div>
        </div>
      </section>

      {/* BLOQUE 6 — LOS 6 BONOS */}
      <section className="py-20 px-4 bg-white" data-id="element-130">
        <div className="max-w-5xl mx-auto" data-id="element-131">
          <div className="text-center mb-12" data-id="element-132">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-brand-violet" data-id="element-133">
              + 6 Bonos Estratégicos
            </h2>
            <p className="text-xl text-gray-600" data-id="element-134">
              Incluidos sin costo extra · Valor real del pack
            </p>
            <div className="inline-block mt-6 bg-red-100 text-red-700 font-bold px-6 py-2 rounded-full border border-red-200 animate-pulse" data-id="element-135">
              🎁 GRATIS POR EL DÍA DE HOY
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-136">
            {[{
            icon: '🏅',
            label: 'Bono 1',
            title: 'Bono 1 — NIVELÓMETRO 360',
            desc: 'El sistema visual de evaluación y promoción de nivel. Sabé exactamente cuándo cada alumna está lista para subir.',
            cover: 'bg-brand-violet',
            spine: 'bg-purple-900'
          }, {
            icon: '💪',
            label: 'Bono 2',
            title: 'Bono 2 — PREPARACIÓN FÍSICA EXPRESS',
            desc: 'Circuitos de fuerza, flexibilidad y resistencia específica. Listos para insertar en cualquier clase.',
            cover: 'bg-brand-gold',
            spine: 'bg-amber-800'
          }, {
            icon: '🥗',
            label: 'Bono 3',
            title: 'Bono 3 — NUTRICIÓN Y RENDIMIENTO',
            desc: 'Guía práctica de alimentación para gimnastas y familias. Sin tecnicismos. Con recetas y listas de compras.',
            cover: 'bg-emerald-700',
            spine: 'bg-emerald-900'
          }, {
            icon: '🚨',
            label: 'Bono 4',
            title: 'Bono 4 — CLASES DE EMERGENCIA',
            desc: '8 clases listas para ejecutar HOY sin preparación previa. Para cuando llueve, no hay aparatos o no preparaste nada.',
            cover: 'bg-rose-700',
            spine: 'bg-rose-900'
          }, {
            icon: '🌟',
            label: 'Bono 5',
            title: 'Bono 5 — KIT DE SUPERVIVENCIA INFANTIL',
            desc: 'Estrategias para grupos de 4 a 7 años sin perder la cordura. Berrinches, llanto, separación y alumnas nuevas. Todo resuelto.',
            cover: 'bg-indigo-700',
            spine: 'bg-indigo-900'
          }, {
            icon: '📅',
            label: 'Bono 6',
            title: 'Bono 6 — PACK DE ORGANIZACIÓN SEMANAL',
            desc: 'El sistema visual para empezar la semana con claridad. Organizador, planificador, control de pagos y agenda anual.',
            cover: 'bg-teal-700',
            spine: 'bg-teal-900'
          }].map((bono, i) => <div key={i} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 flex flex-col items-center text-center" data-id="element-137">
                {/* Book cover mockup */}
                <div className={`relative w-40 h-56 rounded-r-lg rounded-l-md ${bono.cover} shadow-xl overflow-hidden mb-6`} data-id="element-138">
                  <div className={`absolute inset-y-0 left-0 w-3 ${bono.spine}`} data-id="element-139" />
                  <div className="absolute inset-0 pl-6 pr-4 py-5 flex flex-col text-white" data-id="element-140">
                    <div className="text-3xl mb-auto" data-id="element-141">{bono.icon}</div>
                    <div className="text-xl font-bold leading-tight text-left" data-id="element-142">
                      {bono.label}
                    </div>
                    <div className="h-px w-full bg-white/40 my-3" data-id="element-143" />
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-white/80" data-id="element-144">
                      Gimnasia Rítmica 360™
                    </div>
                  </div>
                </div>

                <span className="bg-green-100 text-green-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide mb-4" data-id="element-145">
                  Gratis
                </span>

                <h3 className="font-bold text-lg text-brand-black mb-2 flex items-center justify-center gap-2" data-id="element-146">
                  <span data-id="element-147">{bono.icon}</span> {bono.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-148">{bono.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* BLOQUE 7 — PRECIO Y OFERTA */}
      <section id="pricing" className="py-20 px-4 bg-brand-violet text-white" data-id="element-149">
        <div className="max-w-5xl mx-auto" data-id="element-150">
          <div className="text-center mb-12" data-id="element-151">
            <h2 className="text-3xl font-bold mb-6 text-brand-gold" data-id="element-152">
              ⏰ PRECIO DE LANZAMIENTO — OFERTA POR TIEMPO LIMITADO
            </h2>
            <CountdownTimer data-id="element-153" />
            <p className="text-xl font-semibold text-red-300 bg-red-900/30 inline-block px-6 py-2 rounded-full" data-id="element-154">
              🔴 El precio sube cuando termina el contador
            </p>
          </div>

          {/* Checkout card: selectable pack list + single CTA */}
          <div className="bg-white text-brand-black rounded-3xl p-6 md:p-10 shadow-2xl max-w-2xl mx-auto" data-id="element-155">
            <div className="text-center mb-8" data-id="element-156">
              <h3 className="text-3xl font-bold" data-id="element-157">Elegí tu Pack:</h3>
              <p className="text-gray-500 mt-2" data-id="element-158">
                Ambas opciones incluyen los 6 Bonos estratégicos
              </p>
            </div>

            <div className="space-y-4 mb-8" data-id="element-159">
              {/* Pack Profesional */}
              <button type="button" onClick={() => setSelectedPack('pro')} aria-pressed={selectedPack === 'pro'} className={`w-full text-left rounded-2xl border-2 p-5 flex items-center gap-4 transition-all ${selectedPack === 'pro' ? 'border-brand-violet bg-brand-violet/5' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-160">
                <span className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selectedPack === 'pro' ? 'border-brand-violet' : 'border-gray-300'}`} data-id="element-161">
                  {selectedPack === 'pro' && <span className="w-3 h-3 rounded-full bg-brand-violet" data-id="element-162" />}
                </span>
                <span className="text-3xl" data-id="element-163">📘</span>
                <span className="flex-1 min-w-0" data-id="element-164">
                  <span className="block font-bold text-lg leading-tight" data-id="element-165">
                    PACK PROFESIONAL
                  </span>
                  <span className="block text-sm text-gray-500" data-id="element-166">
                    12 Tomos + 6 Bonos
                  </span>
                </span>
                <span className="text-right flex-shrink-0" data-id="element-167">
                  <span className="block text-gray-400 line-through text-sm" data-id="element-168">
                    $29.990
                  </span>
                  <span className="block text-2xl font-bold text-brand-black" data-id="element-169">
                    $18.990
                  </span>
                </span>
              </button>

              {/* Megapack Elite */}
              <button type="button" onClick={() => setSelectedPack('elite')} aria-pressed={selectedPack === 'elite'} className={`w-full text-left rounded-2xl border-2 p-5 flex items-center gap-4 transition-all ${selectedPack === 'elite' ? 'border-brand-gold bg-brand-gold/10' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-170">
                <span className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selectedPack === 'elite' ? 'border-brand-gold' : 'border-gray-300'}`} data-id="element-171">
                  {selectedPack === 'elite' && <span className="w-3 h-3 rounded-full bg-brand-gold" data-id="element-172" />}
                </span>
                <span className="text-3xl" data-id="element-173">🏆</span>
                <span className="flex-1 min-w-0" data-id="element-174">
                  <span className="block font-bold text-lg leading-tight mb-2" data-id="element-175">
                    MEGAPACK ELITE
                  </span>
                  <span className="flex flex-wrap gap-2 mb-2" data-id="element-176">
                    <span className="inline-flex items-center gap-1 bg-brand-gold text-white text-[11px] font-bold px-2.5 py-1 rounded-full" data-id="element-177">
                      <Star className="w-3 h-3 fill-current" data-id="element-178" /> MÁS ELEGIDO
                    </span>
                    <span className="inline-flex items-center bg-brand-gold/20 text-brand-gold text-[11px] font-bold px-2.5 py-1 rounded-full" data-id="element-179">
                      AHORRÁS $10.000
                    </span>
                  </span>
                  <span className="block text-sm text-gray-500" data-id="element-180">
                    12 Tomos + 6 Bonos + 2 EBOOKS ELITE
                  </span>
                </span>
                <span className="text-right flex-shrink-0" data-id="element-181">
                  <span className="block text-gray-400 line-through text-sm" data-id="element-182">
                    $49.990
                  </span>
                  <span className="block text-2xl font-bold text-brand-gold" data-id="element-183">
                    $27.990
                  </span>
                </span>
              </button>
            </div>

            <button onClick={() => trackFbEvent('InitiateCheckout', {
            content_name: selectedPack === 'elite' ? 'Megapack Elite' : 'Pack Profesional',
            currency: 'ARS',
            value: selectedPack === 'elite' ? 27990 : 18990
          })} className="w-full bg-brand-gold hover:bg-amber-500 text-white font-bold py-5 rounded-2xl transition-colors text-lg md:text-xl shadow-lg flex flex-col items-center justify-center gap-1" data-id="element-184">
              {selectedPack === 'elite' ? 'SÍ, QUIERO EL MEGAPACK ELITE' : 'SÍ, QUIERO EL PACK PROFESIONAL'}
              <ArrowRight className="w-5 h-5" data-id="element-185" />
            </button>

            <p className="text-center text-sm text-gray-500 mt-5 italic" data-id="element-186">
              La opción más elegida por profes de Rítmica argentinos
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mt-12 text-sm text-purple-200" data-id="element-187">
            <span className="flex items-center gap-2" data-id="element-188">
              <Lock className="w-5 h-5" data-id="element-189" /> Pago seguro
            </span>
            <span className="flex items-center gap-2" data-id="element-190">
              <Zap className="w-5 h-5" data-id="element-191" /> Acceso en 5 min
            </span>
            <span className="flex items-center gap-2" data-id="element-192">
              <ShieldCheck className="w-5 h-5" data-id="element-193" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-2" data-id="element-194">
              <CreditCard className="w-5 h-5" data-id="element-195" /> Tarjeta · Transferencia ·
              Mercado Pago
            </span>
          </div>
        </div>
      </section>

      {/* BLOQUE 8 — QUÉ AGREGA EL MEGAPACK ELITE */}
      <section className="py-20 px-4 bg-gray-900 text-white" data-id="element-196">
        <div className="max-w-4xl mx-auto" data-id="element-197">
          <div className="text-center mb-12" data-id="element-198">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-brand-gold" data-id="element-199">
              ¿Qué agrega el Megapack Elite?
            </h2>
            <p className="text-xl text-gray-400" data-id="element-200">
              Por solo $9.000 más, llevás tu club al siguiente nivel.
            </p>
          </div>

          <div className="space-y-6" data-id="element-201">
            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl flex flex-col md:flex-row gap-6 items-start" data-id="element-202">
              <div className="text-5xl" data-id="element-203">📕</div>
              <div data-id="element-204">
                <h3 className="text-2xl font-bold mb-3 text-brand-gold" data-id="element-205">
                  Elite 1 — El Libro Negro de las Coreografías Campeonas
                </h3>
                <p className="text-gray-300 leading-relaxed" data-id="element-206">
                  Ingeniería de puntos, psicología del jurado y diseño
                  estratégico de series de alto impacto. Lo que los clubes
                  ganadores nunca enseñan en los cursos. El sistema para ganar
                  décimas donde nadie más las ve.
                </p>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 p-8 rounded-2xl flex flex-col md:flex-row gap-6 items-start" data-id="element-207">
              <div className="text-5xl" data-id="element-208">📕</div>
              <div data-id="element-209">
                <h3 className="text-2xl font-bold mb-3 text-brand-gold" data-id="element-210">
                  Elite 2 — Sistema de Progresión Rusa
                </h3>
                <p className="text-gray-300 leading-relaxed" data-id="element-211">
                  El método para lograr en 3 meses lo que otros tardan 1 año.
                  Micro-objetivos · Correcciones instantáneas · Flexibilidad
                  extrema segura · Modo competencia. El sistema que usan los
                  clubes de élite y nunca enseñan.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOQUE 9 — COMPARATIVA */}
      <section className="py-20 px-4 bg-white" data-id="element-212">
        <div className="max-w-4xl mx-auto" data-id="element-213">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-214">
            ¿Cuánto cuesta NO tenerlo?
          </h2>

          <div className="overflow-x-auto" data-id="element-215">
            <table className="w-full border-collapse" data-id="element-216">
              <thead data-id="element-217">
                <tr data-id="element-218">
                  <th className="p-4 bg-gray-100 text-gray-600 font-bold text-lg rounded-tl-xl w-1/2 border-r border-white" data-id="element-219">
                    Sin GIMNASIA RÍTMICA 360
                  </th>
                  <th className="p-4 bg-brand-violet text-white font-bold text-lg rounded-tr-xl w-1/2" data-id="element-220">
                    Con GIMNASIA RÍTMICA 360
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-221">
                {[['Horas planificando desde cero cada semana', '5 minutos. Todo listo.'], ['Clases improvisadas sin progresión técnica real', '+200 clases resueltas con progresión por nivel'], ['Grupos sin sistema claro ni continuidad anual', 'Estructura clara para cada nivel y cada semana'], ['No podés justificar la cuota con resultados', 'Sistema documentado que demuestra progreso real'], ['Miedo a que el jurado no reconozca las series', 'El Libro Negro para ganar décimas donde nadie más las ve'], ['Alumnas estancadas año tras año sin avanzar', 'Sistema de Progresión Rusa para triplicar el avance'], ['Sensación de improvisar siempre', 'Llegás a clase con todo resuelto y con confianza']].map((row, i) => <tr key={i} className="border-b border-gray-100" data-id="element-222">
                    <td className="p-4 text-gray-600 bg-gray-50 border-r border-white flex items-start gap-2" data-id="element-223">
                      <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-224" />{' '}
                      {row[0]}
                    </td>
                    <td className="p-4 text-brand-black font-medium bg-purple-50 flex items-start gap-2" data-id="element-225">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-226" />{' '}
                      {row[1]}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-12 p-8 bg-gray-50 rounded-2xl border border-gray-200" data-id="element-227">
            <p className="text-2xl font-bold text-brand-black mb-2" data-id="element-228">
              El lunes podés improvisar o entrar con todo resuelto.
            </p>
            <p className="text-xl text-brand-violet font-semibold" data-id="element-229">
              Vos decidís.
            </p>
          </div>
        </div>
      </section>

      {/* BLOQUE 10 — TESTIMONIOS EXTENDIDOS */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-230">
        <div className="max-w-5xl mx-auto" data-id="element-231">
          <div className="text-center mb-12" data-id="element-232">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-233">
              Lo que dicen los profesores
            </h2>
            <div className="flex justify-center items-center gap-2 text-2xl font-bold text-brand-gold" data-id="element-234">
              4.9/5 <Star className="w-6 h-6 fill-current" data-id="element-235" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-236">
            {[{
            init: 'MV',
            name: 'Marcela V. · Buenos Aires 🇦🇷',
            text: 'Compré un sábado. El domingo a la noche tenía el mes de clases de todos mis grupos listo. Nunca más improviso.'
          }, {
            init: 'CG',
            name: 'Carolina G. · Córdoba 🇦🇷',
            text: 'Tres alumnas de Jardín que estaban estancadas en la pelota progresaron notablemente en el primer mes con el Tomo 1.'
          }, {
            init: 'SR',
            name: 'Silvana R. · Rosario 🇦🇷',
            text: 'Antes tardaba 2 horas armando una clase de Juvenil. Ahora abro el tomo y en 5 minutos tengo todo.'
          }, {
            init: 'PV',
            name: 'Patricia V. · Mendoza 🇦🇷',
            text: 'El Nivelómetro 360 cambió cómo hablo con los padres. Ahora tengo datos concretos para mostrarles el progreso.'
          }, {
            init: 'DA',
            name: 'Daniela A. · Santa Fe 🇦🇷',
            text: 'El Libro Negro me salvó en el torneo provincial. Aplicamos el mapeo del tapiz y subimos casi 2 décimas.'
          }, {
            init: 'LR',
            name: 'Laura R. · Tucumán 🇦🇷',
            text: 'El Sistema de Progresión Rusa cambió cómo corrijo a mis alumnas. Ahora ven su propio avance cada semana.'
          }].map((t, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex gap-4" data-id="element-237">
                <div className="w-12 h-12 bg-brand-violet/10 text-brand-violet rounded-full flex items-center justify-center font-bold flex-shrink-0" data-id="element-238">
                  {t.init}
                </div>
                <div data-id="element-239">
                  <div className="flex text-brand-gold mb-1" data-id="element-240">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-3 h-3 fill-current" data-id="element-241" />)}
                  </div>
                  <p className="text-gray-700 italic mb-2 text-sm" data-id="element-242">
                    "{t.text}"
                  </p>
                  <p className="text-xs font-bold text-gray-500" data-id="element-243">{t.name}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* BLOQUE 11 — GARANTÍA */}
      <section className="py-20 px-4 bg-brand-violet text-white text-center" data-id="element-244">
        <div className="max-w-3xl mx-auto" data-id="element-245">
          <ShieldCheck className="w-20 h-20 mx-auto text-brand-gold mb-6" data-id="element-246" />
          <h2 className="text-3xl md:text-5xl font-bold mb-8" data-id="element-247">
            Garantía 7 días sin preguntas
          </h2>

          <div className="text-xl md:text-2xl leading-relaxed mb-10 text-purple-100" data-id="element-248">
            <p data-id="element-249">Abrí los 12 Tomos. Revisá los 6 Bonos.</p>
            <p data-id="element-250">Usá los Ebooks Elite en tu clase.</p>
            <p className="mt-6 font-bold text-white" data-id="element-251">
              Si no es lo que esperabas →
            </p>
            <p className="font-bold text-brand-gold" data-id="element-252">100% de devolución.</p>
            <p data-id="element-253">Sin preguntas. Sin formularios. Sin drama.</p>
          </div>

          <div className="inline-block border-2 border-white/20 rounded-full px-8 py-3 text-sm font-bold tracking-widest uppercase bg-black/20" data-id="element-254">
            Tasa de devoluciones menor al 2%
          </div>
        </div>
      </section>

      {/* BLOQUE 12 — FAQ */}
      <section className="py-20 px-4 bg-white" data-id="element-255">
        <div className="max-w-3xl mx-auto" data-id="element-256">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-257">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-2" data-id="element-258">
            <FAQItem question="¿Cómo recibo el material?" answer="Al completar el pago recibís un email con el acceso inmediato a todos los PDFs. En menos de 5 minutos podés abrir el primer tomo." data-id="element-259" />
            <FAQItem question="¿Sirve si soy profe nueva o si tengo años de experiencia?" answer="Sí para las dos. Si empezás: te da la estructura que necesitás para no improvisar desde el día 1. Si tenés experiencia: te ahorra el tiempo de planificación y te da herramientas que probablemente no tenés sistematizadas." data-id="element-260" />
            <FAQItem question="¿Los 6 Bonos están en las dos opciones?" answer="Sí. Los 6 Bonos están incluidos tanto en el Pack Profesional como en el Megapack Elite." data-id="element-261" />
            <FAQItem question="¿Qué diferencia hay con otros materiales que ya existen?" answer="GIMNASIA RÍTMICA 360 es el único sistema que cubre las 52 semanas de los 4 niveles con una estructura pedagógica progresiva. No son fichas sueltas. Es un sistema completo de planificación anual." data-id="element-262" />
            <FAQItem question="¿Sirve si tengo grupos de distintos niveles al mismo tiempo?" answer="Exactamente para eso fue diseñado. Cada tomo corresponde a un nivel y una franja del año. Podés trabajar 4 grupos distintos con el mismo sistema." data-id="element-263" />
            <FAQItem question="¿Qué pasa si no me sirve?" answer="Garantía de 7 días. Si por cualquier motivo no es lo que esperabas: te devolvemos el 100% del dinero. Sin preguntas. Sin formularios." data-id="element-264" />
            <FAQItem question="¿Vale la pena el Megapack Elite si ya planifico bien?" answer="Si ya planificás bien: el Elite te da la ventaja competititiva. El Libro Negro de Coreografías y el Sistema de Progresión Rusa son metodologías de alto rendimiento que no se consiguen en ningún otro material en Argentina." data-id="element-265" />
            <FAQItem question="¿Puedo pagar con tarjeta de crédito?" answer="Sí. Aceptamos todas las tarjetas, transferencia bancaria y Mercado Pago." data-id="element-266" />
          </div>
        </div>
      </section>

      {/* BLOQUE 13 — CTA FINAL */}
      <section className="py-20 px-4 bg-brand-black text-white text-center border-t-8 border-brand-violet" data-id="element-267">
        <div className="max-w-4xl mx-auto" data-id="element-268">
          <div className="text-brand-gold text-2xl mb-4" data-id="element-269">🎀</div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-270">
            GIMNASIA RÍTMICA 360
          </h2>
          <p className="text-xl text-gray-400 mb-12" data-id="element-271">
            El sistema de planificación N°1 para profes de Rítmica argentinos.
          </p>

          <blockquote className="text-2xl italic text-gray-300 border-l-4 border-brand-violet pl-6 max-w-2xl mx-auto mb-12 text-left" data-id="element-272">
            "Porque preparar una buena clase no debería costarte todas las
            noches de la semana."
          </blockquote>

          <button onClick={scrollToPricing} className="bg-brand-violet hover:bg-purple-600 text-white text-xl font-bold py-5 px-10 rounded-full shadow-[0_0_30px_rgba(107,33,168,0.5)] transition-all transform hover:scale-105 flex items-center justify-center gap-3 mx-auto w-full md:w-auto mb-8" data-id="element-273">
            💜 QUIERO MI GIMNASIA RÍTMICA 360 AHORA
          </button>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-16" data-id="element-274">
            <span className="flex items-center gap-1" data-id="element-275">
              <ShieldCheck className="w-4 h-4" data-id="element-276" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-277">
              <Lock className="w-4 h-4" data-id="element-278" /> Pago seguro
            </span>
            <span className="flex items-center gap-1" data-id="element-279">
              <Zap className="w-4 h-4" data-id="element-280" /> Acceso inmediato
            </span>
          </div>

          <div className="border-t border-white/10 pt-12 text-sm text-gray-500 space-y-2" data-id="element-281">
            <p className="font-bold text-gray-400" data-id="element-282">🎀 GIMNASIA RÍTMICA 360™</p>
            <p data-id="element-283">📧 contacto@gimnasiaritimica360.com.ar</p>
            <p data-id="element-284">📲 @gimnasiaritimica360</p>
            <p data-id="element-285">🌐 www.gimnasiaritimica360.com.ar</p>
            <p className="mt-6 pt-6 text-xs" data-id="element-286">
              © GIMNASIA RÍTMICA 360™ 2025 — Todos los derechos reservados.
              <br data-id="element-287" />
              Prohibida la reproducción sin autorización expresa.
            </p>
          </div>
        </div>
      </section>
    </div>;
}